/** @jsxImportSource @emotion/react */
import * as R from 'ramda';
import * as React from 'react';
import { css, SerializedStyles} from '@emotion/react';
import copy from '../../assets/icons/copyOutline.svg';
import locationPinRed from '../../assets/icons/locationPinRed.svg';
import { isNilOrEmpty } from '../../language/module';
import { BLACK, COOL_GREY, DEFERRED_ORANGE, RED_CREEK, WHITE } from '../../styles/colors';
import { CLEAR_BUTTON, CLICKABLE, MediumText, UNSELECTABLE_TEXT } from '../../styles/styles';
import { QuickEntitlement } from '../entitlements/models';
import { getActivePassEntitlements, isDeferred, nameWithSeason } from '../entitlements/module';
import { GuestDetails } from '../guestDetails';
import { ellipsizeAtFirstLine } from './guestCardStyles';
import { PassOverviewTable } from './PassOverviewTable';
import ImmutableMap from '../../language/ImmutableMap';
import { EntitlementType } from '../../entitlementTypes/entitlementType';
import {useDispatch, useSelector} from 'react-redux';
import {IAppState} from '../../core/redux/IAppState';
import {useEffect} from 'react';
import {getMediaHistoryAction} from '../entitlements/media/mediaHistory/getMediaHistoryAction';
import { useValidLiftAccessLocations } from '../entitlements/liftAccess/useValidLiftAccessLocations';
import {TooltipFC} from '../../layout/TooltipFC';
import { getResource } from '../../language/resources/getResource';


const styles = {
  root:              css`
    display: flex;
    flex-direction: column;
    justify-content: center;
  `,
  listItem:          css`
    display: block; 
    ${MediumText}; 
    font-weight: bold; 
    ${ellipsizeAtFirstLine};
  `,
  passOverviewTable: css`
    padding: 15px;
    max-height: 300px;
    width: 300px;
    overflow-y: auto;
  `,
  noPassMsg:         css`
    display: block; 
    ${MediumText};
    font-weight: bold;
  `,
  mediaId:           css`
    ${MediumText}; 
    letter-spacing: 2px;
  `,
  copyMediaIdBtn:    css`
    height: 20px;
    width: 25px;
    display: inline-block;
    margin-bottom: -2px; /* Align with text to the side */
    ${UNSELECTABLE_TEXT};
    ${CLEAR_BUTTON};
    ${CLICKABLE};
  `,
  copyBtn:           css`
    width: 17px;
    height: 17px;
    margin-bottom: -2px; /* Align with text on the side */
  `,
  locationPinWrapper:   css`
    display: inline-flex;
    align-items: center;
  `,
  locationPinTooltipText: css`
    display: block;
    color: ${BLACK};

    /* Cap long location lists and scroll inside the tooltip. Scroll lives here (not the outer box) so the
       hover bridge below isn't clipped by overflow. */
    max-height: 40vh;
    overflow-y: auto;
  `,
  locationPin:          css`
    height: 1.5em;
    vertical-align: middle;
    margin-left: 4px;
  `,
};

const renderPasses = (
  passEntitlements: QuickEntitlement[],
  allEntitlementTypes: ImmutableMap<EntitlementType> | undefined,
  hotlistedPassIds: number[],
  limit: number,
  onPinHoverChange: (hovered: boolean) => void,
  restrictionTextFor: (entitlementTypeId: number) => string,
  shouldShowLocationPin: (pass: QuickEntitlement) => boolean,
): React.ReactNode[] => {

  const toColor = (hotListed: boolean, deferred: boolean) =>
    deferred ? DEFERRED_ORANGE : hotListed ? RED_CREEK : BLACK;

  return R.take(limit, passEntitlements).map(
    pass => {
      const hotlisted = R.any(passId => passId === pass.entitlementId, hotlistedPassIds);
      const deferred = isDeferred(pass);

      const restrictionText = restrictionTextFor(pass.entitlementTypeId);

      return (
      <div
        key={pass.entitlementId}
        id={`entitlement-${pass.entitlementId}`}
        css={css`
          display: flex;
          align-items: center;
          color: ${toColor(hotlisted, deferred)};
        `}
      >
        <span css={css`${styles.listItem}; min-width: 0;`}>
          {nameWithSeason(pass, allEntitlementTypes || ImmutableMap.empty())}
        </span>
        {shouldShowLocationPin(pass) &&
        <span
          onMouseEnter={() => onPinHoverChange(true)}
          onMouseLeave={() => onPinHoverChange(false)}
          css={styles.locationPinWrapper}
        >
          <TooltipFC
            width={300}
            autoHide={false}
            escapeOverflow
            contentCss={css`
              background-color: ${WHITE};
              border: 1px solid ${COOL_GREY};
              border-radius: 0;

              /* Transparent bridge over the gap between the pin and the tooltip so moving the pointer into the
                 tooltip (to scroll) doesn't cross a dead zone and dismiss it. No layout impact. */

              &::before {
                content: '';
                position: absolute;
                left: 0;
                right: 0;
                top: -8px;
                height: 8px;
              }
            `}
            content={
              <span css={styles.locationPinTooltipText}>
                {restrictionText}
              </span>
            }
          >
            <img src={locationPinRed} alt='Location pin' css={styles.locationPin} />
          </TooltipFC>
        </span>
        }
      </div>
    )},
  );
};

type Props = {
  guest: GuestDetails;
  maximumLength: number;
  passEntitlementId: number;
  copyToClipboard: (text: string) => void;
  hotlistedPassIds: number[];
  allEntitlementTypes?: ImmutableMap<EntitlementType>;
  rootCss?: SerializedStyles;
};

/**
 * PassOverview: a component that renders a guest's pass information -- would be the list of passes the guest has,
 * and their Ikon Pass Media ID if available.
 *
 * If no passes are available, shows a message for that.
 * If no media ID is available, shows a message for that.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
const PassOverView: React.FC<Props> = (props: Props) => {
  const dispatch = useDispatch();
  const { passId, guest } = useSelector((state: IAppState) => {
    return {
      guest: state.guests.get(props.guest.masterGuestId),
      passId: state.mediaHistories.find(guest => guest.guestId === props.guest.masterGuestId)?.mediaID
  }
  })

  const passes    = React.useMemo(
    () => getActivePassEntitlements(props.passEntitlementId, props.guest),
    [props.guest, props.passEntitlementId],
  );

  const resources = useSelector((state: IAppState) => state.locale);

  const [pinHovered, setPinHovered] = React.useState(false);

  // Loads the valid locations for this guest's location-restricted passes and derives pin visibility + tooltip text.
  const { shouldShowLocationPin, restrictionTextFor } = useValidLiftAccessLocations(passes);

  useEffect(() => {
    if (guest.media.find(media => media.statusCode === 1)?.mediaID !== passId) {
      dispatch(getMediaHistoryAction(props.guest.masterGuestId))
    }
  }, [guest, dispatch, passId, props.guest.masterGuestId]);


  const onCopyClick = event => {
    event.preventDefault();
    event.stopPropagation();

    props.copyToClipboard(passId || getResource(resources, 'PASS_OVERVIEW_NO_MEDIA'));
  };

  return (
    <div css={css`${styles.root} ${props.rootCss}`}>
      <TooltipFC width={330} disabled={pinHovered} content={
        <PassOverviewTable
          entitlements={getActivePassEntitlements(props.passEntitlementId, props.guest)}
          allEntitlementTypes={props.allEntitlementTypes}
          extraCss={styles.passOverviewTable}
        />
      }
        children={
        isNilOrEmpty(passes)
        ? <span css={styles.noPassMsg} id={`entitlement-${props.guest.masterGuestId}`} >
        {getResource(resources, 'PASS_OVERVIEW_NO_ENTITLEMENTS')}
        </span>
        : renderPasses(
        passes, props.allEntitlementTypes, props.hotlistedPassIds, props.maximumLength, setPinHovered,
        restrictionTextFor, shouldShowLocationPin)
      }
      />
      <span title={getResource(resources, 'PASS_OVERVIEW_TITLE')} css={styles.mediaId}>
        {passId || getResource(resources, 'PASS_OVERVIEW_NO_MEDIA')}
        {
          !isNilOrEmpty(passId) && (
            <button title={getResource(resources, 'PASS_OVERVIEW_COPY_BUTTON')} onClick={onCopyClick} css={styles.copyMediaIdBtn}>
              <img src={copy} alt='Copy' css={styles.copyBtn} />
            </button>
          )
        }
        </span>
    </div>
  );
};

export default PassOverView;
